import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Plus, Award, ExternalLink, Upload, Download, FileText, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LearningPage = () => {
  const { user } = useAuth();
  const isAdmin = ['admin', 'hr', 'manager'].includes(user?.role);
  const [learning, setLearning] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [providerFilter, setProviderFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [docFile, setDocFile] = useState(null);
  const [certFile, setCertFile] = useState(null);
  const [newLearning, setNewLearning] = useState({
    course_name: '', provider: '', certification_name: '', completion_date: '', certificate_url: '', credits: 0
  });

  const fetchData = useCallback(async () => {
    try {
      let url = `${API_URL}/api/learning`;
      if (providerFilter !== 'all') url += `?provider=${providerFilter}`;
      const [learningRes, statsRes] = await Promise.all([
        axios.get(url, { withCredentials: true }),
        axios.get(`${API_URL}/api/learning/stats`, { withCredentials: true })
      ]);
      setLearning(learningRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load learning data');
    } finally {
      setLoading(false);
    }
  }, [providerFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uploadFile = async (file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    const res = await axios.post(`${API_URL}/api/upload?category=${category}`, formData, {
      withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data.path;
  };

  const handleAddLearning = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      let documentUrl = '';
      let certificateFileUrl = '';
      if (docFile) documentUrl = await uploadFile(docFile, 'learning_doc');
      if (certFile) certificateFileUrl = await uploadFile(certFile, 'learning_cert');
      await axios.post(`${API_URL}/api/learning`, {
        ...newLearning,
        credits: parseFloat(newLearning.credits) || 0,
        document_url: documentUrl || undefined,
        certificate_file_url: certificateFileUrl || undefined,
      }, { withCredentials: true });
      toast.success('Learning record added');
      setIsAddDialogOpen(false);
      setNewLearning({ course_name: '', provider: '', certification_name: '', completion_date: '', certificate_url: '', credits: 0 });
      setDocFile(null);
      setCertFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add learning record');
    } finally {
      setUploading(false);
    }
  };

  const getProviderBadge = (provider) => {
    const colors = { AWS: 'bg-orange-500/20 text-orange-500', GCP: 'bg-blue-500/20 text-blue-500', OCI: 'bg-red-500/20 text-red-500', Coursera: 'badge-ai', Udemy: 'badge-warning' };
    return <Badge className={colors[provider] || 'bg-muted'}>{provider}</Badge>;
  };

  const totalCredits = learning.reduce((sum, l) => sum + (l.credits || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="learning-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><GraduationCap className="w-7 h-7 text-primary" />Continuous Learning</h1>
          <p className="text-muted-foreground">Track certifications, courses, and training credits</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-learning-btn"><Plus className="w-4 h-4" />Add Learning</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle>Add Learning Record</DialogTitle></DialogHeader>
            <form onSubmit={handleAddLearning} className="space-y-4">
              <div className="space-y-2"><Label>Course Name</Label><Input value={newLearning.course_name} onChange={(e) => setNewLearning({ ...newLearning, course_name: e.target.value })} required data-testid="learning-course" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Provider</Label>
                  <Select value={newLearning.provider} onValueChange={(v) => setNewLearning({ ...newLearning, provider: v })}>
                    <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AWS">AWS</SelectItem><SelectItem value="GCP">GCP</SelectItem>
                      <SelectItem value="OCI">OCI</SelectItem><SelectItem value="Coursera">Coursera</SelectItem>
                      <SelectItem value="Udemy">Udemy</SelectItem><SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Credits</Label><Input type="number" value={newLearning.credits} onChange={(e) => setNewLearning({ ...newLearning, credits: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Certification Name</Label><Input value={newLearning.certification_name} onChange={(e) => setNewLearning({ ...newLearning, certification_name: e.target.value })} placeholder="e.g., AWS Solutions Architect" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Completion Date</Label><Input type="date" value={newLearning.completion_date} onChange={(e) => setNewLearning({ ...newLearning, completion_date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Certificate URL</Label><Input value={newLearning.certificate_url} onChange={(e) => setNewLearning({ ...newLearning, certificate_url: e.target.value })} placeholder="https://..." /></div>
              </div>
              {/* Document Upload */}
              <div className="space-y-2">
                <Label>Upload Course Document (optional)</Label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-3 text-center text-sm transition-colors ${docFile ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                      {docFile ? <span className="flex items-center justify-center gap-1.5"><FileText className="w-4 h-4" />{docFile.name}</span> : <span className="flex items-center justify-center gap-1.5"><Upload className="w-4 h-4 text-muted-foreground" />Choose document</span>}
                    </div>
                    <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setDocFile(e.target.files[0] || null)} data-testid="learning-doc-upload" />
                  </label>
                  {docFile && <Button type="button" size="icon" variant="ghost" onClick={() => setDocFile(null)}><X className="w-4 h-4" /></Button>}
                </div>
              </div>
              {/* Certificate Upload */}
              <div className="space-y-2">
                <Label>Upload Certificate (optional)</Label>
                <div className="flex items-center gap-2">
                  <label className="flex-1 cursor-pointer">
                    <div className={`border-2 border-dashed rounded-lg p-3 text-center text-sm transition-colors ${certFile ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : 'border-border hover:border-primary/50'}`}>
                      {certFile ? <span className="flex items-center justify-center gap-1.5"><Award className="w-4 h-4" />{certFile.name}</span> : <span className="flex items-center justify-center gap-1.5"><Upload className="w-4 h-4 text-muted-foreground" />Choose certificate</span>}
                    </div>
                    <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={(e) => setCertFile(e.target.files[0] || null)} data-testid="learning-cert-upload" />
                  </label>
                  {certFile && <Button type="button" size="icon" variant="ghost" onClick={() => setCertFile(null)}><X className="w-4 h-4" /></Button>}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={uploading} data-testid="submit-learning">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</> : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Courses</p><p className="text-2xl font-bold">{learning.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Credits</p><p className="text-2xl font-bold">{totalCredits}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Certifications</p><p className="text-2xl font-bold">{learning.filter(l => l.certification_name).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Providers</p><p className="text-2xl font-bold">{stats.length}</p></CardContent></Card>
      </div>

      <div className="flex gap-4">
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Filter by provider" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            <SelectItem value="AWS">AWS</SelectItem><SelectItem value="GCP">GCP</SelectItem>
            <SelectItem value="OCI">OCI</SelectItem><SelectItem value="Coursera">Coursera</SelectItem><SelectItem value="Udemy">Udemy</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {learning.map((item) => (
          <Card key={item.id}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-2">
                {getProviderBadge(item.provider)}
                <div className="flex gap-1">
                  {item.document_url && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="Download document" onClick={() => window.open(`${API_URL}/api/files/${item.document_url}`, '_blank')}>
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                  {item.certificate_file_url && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600" title="View certificate" onClick={() => window.open(`${API_URL}/api/files/${item.certificate_file_url}`, '_blank')}>
                      <Award className="w-4 h-4" />
                    </Button>
                  )}
                  {item.certificate_url && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" title="External link" onClick={() => window.open(item.certificate_url, '_blank')}>
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              <h3 className="font-semibold mt-3">{item.course_name}</h3>
              {isAdmin && item.employee_name && (
                <p className="text-sm text-primary font-medium mt-0.5">By: {item.employee_name}</p>
              )}
              {item.certification_name && (
                <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                  <Award className="w-4 h-4" />{item.certification_name}
                </div>
              )}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                {item.completion_date && <p className="text-xs text-muted-foreground">{new Date(item.completion_date).toLocaleDateString()}</p>}
                <div className="flex gap-2">
                  {item.credits > 0 && <Badge variant="outline">{item.credits} credits</Badge>}
                  {item.document_url && <Badge variant="outline" className="text-xs gap-1"><FileText className="w-3 h-3" />Doc</Badge>}
                  {item.certificate_file_url && <Badge variant="outline" className="text-xs gap-1 text-green-600"><Award className="w-3 h-3" />Cert</Badge>}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {learning.length === 0 && (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No learning records found</CardContent></Card>
      )}
    </div>
  );
};

export default LearningPage;
